import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import {
  generateKeyPair,
  exportPublicKeyB64,
  importPeerPublicKey,
  wrapPrivateKey,
  unwrapPrivateKey,
  deriveSharedKey,
  encryptText,
  decryptText,
  encryptBlob,
  decryptBlob,
  wrapFileKey,
  unwrapFileKey,
  isE2ESupported,
} from '../lib/e2e';

const E2EContext = createContext(null);
const SESSION_STORAGE_KEY = 'gtc_e2e_priv_jwk';
const LOCAL_STORAGE_KEY = 'gtc_e2e_priv_jwk_persist';
const REMEMBER_DAYS = 7;
const ECDH_NAMED_CURVE = 'P-256';

async function persistPrivateKey(privateKey, publicKeyB64, remember = false) {
  try {
    const jwk = await crypto.subtle.exportKey('jwk', privateKey);
    const payload = JSON.stringify({ jwk, pub: publicKeyB64 });
    if (remember) {
      const expiresAt = Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ payload, expiresAt }));
      // Session copy is also useful for the current tab
      sessionStorage.setItem(SESSION_STORAGE_KEY, payload);
    } else {
      sessionStorage.setItem(SESSION_STORAGE_KEY, payload);
      // If a stale persistent copy exists, wipe it (user opted-out this time)
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  } catch (e) { console.warn('[E2E] persistPrivateKey failed:', e?.message || e); }
}

async function restorePrivateKey() {
  try {
    // 1) Try localStorage (persistent, respects 7-day expiry)
    const persisted = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (persisted) {
      const { payload, expiresAt } = JSON.parse(persisted);
      if (typeof expiresAt === 'number' && Date.now() < expiresAt) {
        const { jwk, pub } = JSON.parse(payload);
        const key = await crypto.subtle.importKey(
          'jwk', jwk, { name: 'ECDH', namedCurve: ECDH_NAMED_CURVE }, true, ['deriveKey', 'deriveBits']
        );
        // Refresh session copy so subsequent reloads stay fast even after localStorage is wiped
        sessionStorage.setItem(SESSION_STORAGE_KEY, payload);
        return { privateKey: key, publicKeyB64: pub };
      }
      // Expired → cleanup
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
    // 2) Fall back to sessionStorage (tab-scoped)
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const { jwk, pub } = JSON.parse(raw);
    const key = await crypto.subtle.importKey(
      'jwk', jwk, { name: 'ECDH', namedCurve: ECDH_NAMED_CURVE }, true, ['deriveKey', 'deriveBits']
    );
    return { privateKey: key, publicKeyB64: pub };
  } catch (e) {
    console.warn('[E2E] restorePrivateKey failed:', e?.message || e);
    return null;
  }
}

function clearPersistedPrivateKey() {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (e) { console.warn('[E2E] clear persisted failed:', e?.message || e); }
}

export function E2EProvider({ children }) {
  const privateKeyRef = useRef(null); // CryptoKey (non-extractable in memory)
  const publicKeyB64Ref = useRef(null);
  const sharedKeyCache = useRef(new Map()); // peerId -> CryptoKey
  const peerPubCache = useRef(new Map());   // peerId -> b64 pubkey (last known)
  const [ready, setReady] = useState(false);
  const [supported] = useState(isE2ESupported);
  const [needsUnlock, setNeedsUnlock] = useState(false);

  const reset = useCallback(() => {
    privateKeyRef.current = null;
    publicKeyB64Ref.current = null;
    sharedKeyCache.current.clear();
    peerPubCache.current.clear();
    clearPersistedPrivateKey();
    setReady(false);
    setNeedsUnlock(false);
  }, []);

  /**
   * On mount, try to restore private key from sessionStorage (survives reloads within tab).
   * If restore fails but the server has stored E2E keys for this user → set needsUnlock=true
   * so the UI can show an unlock prompt (password required to derive the wrap key again).
   */
  useEffect(() => {
    if (!supported) return;
    (async () => {
      const restored = await restorePrivateKey();
      if (restored) {
        privateKeyRef.current = restored.privateKey;
        publicKeyB64Ref.current = restored.publicKeyB64;
        setReady(true);
        return;
      }
      // No cached private key. Check whether the server has E2E keys for us.
      // If yes → we need the user to re-enter their password to unlock.
      try {
        const token = localStorage.getItem('gtc_token');
        if (!token) return; // Not logged in, nothing to unlock
        const { data } = await api.get('/keys/me');
        if (data && data.has_keys) setNeedsUnlock(true);
      } catch (e) { console.warn('[E2E] check /keys/me failed:', e?.message || e); }
    })();
  }, [supported]);

  /**
   * Unlock the existing wrapped private key by re-entering the password.
   * Used by the "🔒 Sblocca crittografia" modal after page reload / new tab.
   */
  const unlock = useCallback(async (password, remember = false) => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    try {
      const { data } = await api.get('/keys/me');
      if (!data.has_keys) return { ok: false, reason: 'no_keys' };
      const priv = await unwrapPrivateKey(
        {
          wrapped_private_key: data.wrapped_private_key,
          key_salt: data.key_salt,
          key_iv: data.key_iv,
        },
        password,
      );
      privateKeyRef.current = priv;
      publicKeyB64Ref.current = data.public_key;
      await persistPrivateKey(priv, data.public_key, remember);
      setReady(true);
      setNeedsUnlock(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'wrong_password', error: e };
    }
  }, [supported]);

  /**
   * Called from AuthContext right after login (or on refresh).
   * - If the account has no keys yet → generate + upload (wrapped with password).
   * - If keys exist → unlock the private key with the password.
   * - If unlock fails (e.g. password was reset by admin) → rotate: generate new keypair,
   *   upload with current password. Old ciphertexts remain unreadable (true E2E behaviour).
   */
  const initialize = useCallback(async (password) => {
    if (!supported) { setReady(false); return { ok: false, reason: 'unsupported' }; }
    try {
      const { data } = await api.get('/keys/me');
      if (data.has_keys) {
        try {
          const priv = await unwrapPrivateKey(
            {
              wrapped_private_key: data.wrapped_private_key,
              key_salt: data.key_salt,
              key_iv: data.key_iv,
            },
            password,
          );
          privateKeyRef.current = priv;
          publicKeyB64Ref.current = data.public_key;
          await persistPrivateKey(priv, data.public_key);
          setReady(true);
          return { ok: true, rotated: false };
        } catch (unlockErr) {
          // Password mismatch (probably reset). Rotate keys.
          console.warn('[E2E] Unlock failed, rotating keys');
        }
      }
      // Generate & upload
      const kp = await generateKeyPair();
      const pubB64 = await exportPublicKeyB64(kp.publicKey);
      const wrapped = await wrapPrivateKey(kp.privateKey, password);
      await api.post('/keys/setup', {
        public_key: pubB64,
        wrapped_private_key: wrapped.wrapped_private_key,
        key_salt: wrapped.key_salt,
        key_iv: wrapped.key_iv,
      });
      privateKeyRef.current = kp.privateKey;
      publicKeyB64Ref.current = pubB64;
      await persistPrivateKey(kp.privateKey, pubB64);
      setReady(true);
      return { ok: true, rotated: !!(await api.get('/keys/me').catch(() => null)) };
    } catch (e) {
      console.error('[E2E] initialize failed', e);
      setReady(false);
      return { ok: false, reason: 'error', error: e };
    }
  }, [supported]);

  /** Rotate keys after a password change (client-side rewrap). */
  const rotateForPassword = useCallback(async (newPassword) => {
    if (!supported || !privateKeyRef.current) return { ok: false };
    // Web Crypto doesn't let us re-export a private key unless it was created extractable.
    // Our generateKeyPair() creates extractable keys, so this works.
    try {
      const wrapped = await wrapPrivateKey(privateKeyRef.current, newPassword);
      await api.post('/keys/setup', {
        public_key: publicKeyB64Ref.current,
        wrapped_private_key: wrapped.wrapped_private_key,
        key_salt: wrapped.key_salt,
        key_iv: wrapped.key_iv,
      });
      return { ok: true };
    } catch (e) {
      console.error('[E2E] rotate failed', e);
      return { ok: false, error: e };
    }
  }, [supported]);

  const getSharedKey = useCallback(async (peerId) => {
    if (!privateKeyRef.current) throw new Error('E2E not initialized');
    if (sharedKeyCache.current.has(peerId)) return sharedKeyCache.current.get(peerId);
    const { data } = await api.get(`/keys/user/${peerId}`);
    if (!data.has_keys) throw new Error('Peer has no E2E keys yet');
    const peerPub = await importPeerPublicKey(data.public_key);
    const shared = await deriveSharedKey(privateKeyRef.current, peerPub);
    sharedKeyCache.current.set(peerId, shared);
    peerPubCache.current.set(peerId, data.public_key);
    return shared;
  }, []);

  const peerHasKeys = useCallback(async (peerId) => {
    try {
      const { data } = await api.get(`/keys/user/${peerId}`);
      return !!data.has_keys;
    } catch { return false; }
  }, []);

  // --------------- High-level ops used by ChatView ---------------
  const encryptForPeer = useCallback(async (peerId, plaintext) => {
    const shared = await getSharedKey(peerId);
    return encryptText(shared, plaintext);
  }, [getSharedKey]);

  const decryptFromPeer = useCallback(async (peerId, payload) => {
    const shared = await getSharedKey(peerId);
    return decryptText(shared, payload);
  }, [getSharedKey]);

  const encryptFileForPeer = useCallback(async (peerId, blob) => {
    const shared = await getSharedKey(peerId);
    const { cipherBlob, fileKey, iv } = await encryptBlob(blob);
    const wrapped = await wrapFileKey(shared, fileKey);
    // Convert iv to base64 for transport
    let ivB64 = '';
    {
      let bin = '';
      for (let i = 0; i < iv.byteLength; i++) bin += String.fromCharCode(iv[i]);
      ivB64 = btoa(bin);
    }
    return {
      cipherBlob,
      crypto: {
        v: 1,
        file_iv: ivB64,
        file_key_wrapped: wrapped.file_key_wrapped,
        file_key_iv: wrapped.file_key_iv,
      },
    };
  }, [getSharedKey]);

  const decryptFileFromPeer = useCallback(async (peerId, cipherBlob, cryptoMeta, outputMime) => {
    const shared = await getSharedKey(peerId);
    const rawKey = await unwrapFileKey(shared, cryptoMeta.file_key_wrapped, cryptoMeta.file_key_iv);
    // iv
    const ivB64 = cryptoMeta.file_iv;
    const bin = atob(ivB64);
    const iv = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) iv[i] = bin.charCodeAt(i);
    return decryptBlob(cipherBlob, rawKey, iv, outputMime || 'application/octet-stream');
  }, [getSharedKey]);

  const value = useMemo(() => ({
    supported,
    ready,
    needsUnlock,
    initialize,
    unlock,
    reset,
    rotateForPassword,
    peerHasKeys,
    encryptForPeer,
    decryptFromPeer,
    encryptFileForPeer,
    decryptFileFromPeer,
  }), [supported, ready, needsUnlock, initialize, unlock, reset, rotateForPassword, peerHasKeys, encryptForPeer, decryptFromPeer, encryptFileForPeer, decryptFileFromPeer]);

  return <E2EContext.Provider value={value}>{children}</E2EContext.Provider>;
}

export const useE2E = () => useContext(E2EContext);
